import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Empty } from "antd";
import { formatWithCommas } from "../../../assets/helpers";
import "./TrendAreaChart.scss";

const SERIES_COLORS = ["#36a546", "#ffa52f", "#6287ff"];

/**
 * Smooth gradient area chart used for the month-over-month supervisor trend.
 * Series can be toggled from the custom legend chips below the plot.
 */
const TrendAreaChart = ({
  categories = [],
  seriesData = [],
  colors = SERIES_COLORS,
  height = 280,
}) => {
  const [hidden, setHidden] = useState(() => new Set());

  const visibleSeries = useMemo(
    () => seriesData.filter((s) => !hidden.has(s.name)),
    [seriesData, hidden],
  );

  const visibleColors = useMemo(
    () =>
      seriesData
        .map((s, i) => ({ name: s.name, color: colors[i % colors.length] }))
        .filter((s) => !hidden.has(s.name))
        .map((s) => s.color),
    [seriesData, hidden, colors],
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "Gilroy, Inter, sans-serif",
        parentHeightOffset: 0,
      },
      colors: visibleColors,
      stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.28,
          opacityTo: 0.02,
          stops: [0, 92, 100],
        },
      },
      markers: {
        /* a one or two point series has no line to read, so show the points */
        size: categories.length <= 2 ? 6 : 0,
        strokeWidth: 2,
        strokeColors: "#fff",
        hover: { size: 6 },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        labels: {
          style: {
            colors: "#838a9e",
            fontSize: "11px",
            fontWeight: 600,
            fontFamily: "Gilroy, Inter, sans-serif",
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#a3a9bb",
            fontSize: "11px",
            fontWeight: 600,
            fontFamily: "Gilroy, Inter, sans-serif",
          },
          formatter: (v) => formatWithCommas(Math.round(v)),
        },
      },
      grid: {
        borderColor: "#eff1f8",
        strokeDashArray: 5,
        xaxis: { lines: { show: false } },
        padding: { left: 4, right: 8, top: -10 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ series, dataPointIndex, w }) => {
          const rows = series
            .map((s, i) => {
              const name = w.globals.seriesNames[i];
              const color = w.globals.colors[i];
              return (
                '<div class="dsh-chart-tip__row">' +
                '<span><i class="dsh-chart-tip__dot" style="background:' +
                color +
                '"></i>' +
                name +
                "</span><b>" +
                formatWithCommas(s[dataPointIndex] ?? 0) +
                "</b></div>"
              );
            })
            .join("");
          return (
            '<div class="dsh-chart-tip">' +
            '<div class="dsh-chart-tip__head">' +
            (categories[dataPointIndex] || "") +
            "</div>" +
            rows +
            "</div>"
          );
        },
      },
    }),
    [categories, visibleColors],
  );

  const toggle = (name) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else if (next.size < seriesData.length - 1) next.add(name);
      return next;
    });
  };

  if (!categories.length || !seriesData.length) {
    return (
      <div className="dsh-trend dsh-trend--empty" style={{ minHeight: height }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No trend data available"
        />
      </div>
    );
  }

  return (
    <div className="dsh-trend">
      <ReactApexChart
        options={options}
        series={visibleSeries}
        type="area"
        height={height}
      />
      <div className="dsh-trend__legend">
        {seriesData.map((s, i) => {
          const isOff = hidden.has(s.name);
          const totalValue = (s.data || []).reduce(
            (a, b) => a + (Number(b) || 0),
            0,
          );
          return (
            <button
              type="button"
              key={s.name}
              className={`dsh-trend__chip ${isOff ? "is-off" : ""}`}
              onClick={() => toggle(s.name)}
            >
              <i style={{ background: colors[i % colors.length] }} />
              <span>{s.name}</span>
              <b>{formatWithCommas(totalValue)}</b>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TrendAreaChart;
